import { Avatar, Button, type CommandGroup, CommandPalette, Kbd, toast } from "@nudgebox/ui";
import { AppsIcon, BellIcon, BillingIcon, ChannelIcon, CheckIcon, CopyIcon, DarkThemeIcon, DocsIcon, HomeIcon, KeyIcon, LightThemeIcon, LogOutIcon, PlusIcon, SearchIcon, SendIcon, PasskeyIcon, SettingsIcon, SystemThemeIcon, UsersIcon } from "@nudgebox/ui/icons";
import "@nudgebox/ui/tokens.stylex";
import { color, font, fontSize, media, motion, radius, space } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { CreateApplicationDialog } from "@/features/applications/CreateApplicationDialog.tsx";
import { applicationsQuery, organizationsQuery } from "@/lib/queries.ts";
import { setThemePreference, type ThemePreference, useThemePreference } from "@/lib/theme.ts";
import { useSignOut } from "./useSignOut.ts";
const RECENTS_KEY = "nudgebox:command-recents";
const MAX_RECENTS = 4;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
const modKey = isMac ? "⌘" : "Ctrl";

/**
 * The ⌘K / Ctrl+K command palette for the organization shell: jump to any
 * page or application, run the current application's common actions, switch
 * organizations and change preferences without leaving the keyboard.
 */
export function CommandMenu({
  orgSlug,
  variant = "field"
}: {
  orgSlug: string;
  /** `field`: sidebar search box with shortcut hint. `icon`: compact button for the mobile top bar. */
  variant?: "field" | "icon";
}) {
  const [open, setOpen] = useState(false);
  const [creatingApp, setCreatingApp] = useState(false);
  const [recentIds, setRecentIds] = useState(readRecents);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "k" || event.altKey || event.shiftKey) return;
      if (!(isMac ? event.metaKey : event.ctrlKey)) return;
      event.preventDefault();
      setOpen(current => !current);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  const groups = useCommandGroups(orgSlug, () => setCreatingApp(true));
  return <>
      {variant === "icon" ? <Button variant="ghost" iconOnly icon={<SearchIcon size={20} />} aria-label="Search" onClick={() => setOpen(true)} /> : <button type="button" onClick={() => setOpen(true)} className="x78zum5 x6s0dn4 x1a62zme xh8yej3 x10w6t97 x8tjdam x1uu5im9 xwx2izl xmkeg23 x1y0btm7 x4bnxre x1e1nhgj xoazux2 x1paulmi xxe3v3u x1wv6yj xluwven x1ypdohk x1de99jn x1t137rt x9v5kkp x15xi569 x1y3gkto xshfolx x7updtu">
          <SearchIcon size={14} />
          <span className="x1iyjqo2 xdpxx8g">Search…</span>
          <span className="x78zum5 xeca93d x92x8s6" aria-hidden>
            <Kbd>{modKey}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>}

      <CommandPalette open={open} onOpenChange={setOpen} groups={groups} recentIds={recentIds} onItemSelect={item => {
      // Toggles (theme) aren't destinations worth surfacing again.
      if (item.id.startsWith("theme:")) return;
      const id = item.id.replace(/^recent:/, "");
      const next = [id, ...recentIds.filter(existing => existing !== id)].slice(0, MAX_RECENTS);
      setRecentIds(next);
      writeRecents(next);
    }} />

      <CreateApplicationDialog orgSlug={orgSlug} open={creatingApp} onOpenChange={setCreatingApp} />
    </>;
}
function useCommandGroups(orgSlug: string, createApplication: () => void): CommandGroup[] {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const theme = useThemePreference();
  const {
    data: applications
  } = useSuspenseQuery(applicationsQuery(orgSlug));
  const {
    data: organizations
  } = useSuspenseQuery(organizationsQuery);
  // Loose params: present only while inside an application's pages.
  const {
    appSlug
  } = useParams({
    strict: false
  });
  const currentApp = applications.find(app => app.slug === appSlug);
  const params = {
    orgSlug
  };
  const groups: CommandGroup[] = [];
  if (currentApp) {
    const appParams = {
      orgSlug,
      appSlug: currentApp.slug
    };
    const keywords = [currentApp.name, currentApp.slug];
    groups.push({
      label: currentApp.name,
      items: [{
        id: `app:${currentApp.slug}:compose`,
        label: "Send notification",
        icon: <SendIcon size={15} />,
        keywords: [...keywords, "new", "compose", "push", "message"],
        onSelect: () => void navigate({
          to: "/$orgSlug/apps/$appSlug/notifications/new",
          params: appParams
        })
      }, {
        id: `app:${currentApp.slug}:notifications`,
        label: "Notifications",
        icon: <BellIcon size={15} />,
        keywords: [...keywords, "history", "sent", "deliveries"],
        onSelect: () => void navigate({
          to: "/$orgSlug/apps/$appSlug/notifications",
          params: appParams
        })
      }, {
        id: `app:${currentApp.slug}:subscribers`,
        label: "Subscribers",
        icon: <UsersIcon size={15} />,
        keywords: [...keywords, "audience", "devices"],
        onSelect: () => void navigate({
          to: "/$orgSlug/apps/$appSlug/subscribers",
          params: appParams,
          search: {}
        })
      }, {
        id: `app:${currentApp.slug}:channels`,
        label: "Channels",
        icon: <ChannelIcon size={15} />,
        keywords: [...keywords, "topics"],
        onSelect: () => void navigate({
          to: "/$orgSlug/apps/$appSlug/channels",
          params: appParams
        })
      }, {
        id: `app:${currentApp.slug}:settings`,
        label: "Application settings",
        icon: <SettingsIcon size={15} />,
        keywords: [...keywords, "rename", "icon", "website", "delete"],
        onSelect: () => void navigate({
          to: "/$orgSlug/apps/$appSlug/settings",
          params: appParams
        })
      }, {
        id: `app:${currentApp.slug}:copy-link`,
        label: "Copy subscription link",
        icon: <CopyIcon size={15} />,
        keywords: [...keywords, "url", "share", "qr", "clipboard"],
        onSelect: () => void copySubscribeUrl(currentApp.subscribeUrl)
      }]
    });
  }
  groups.push({
    label: "Applications",
    items: [...applications.map(app => ({
      id: `app:${app.slug}`,
      label: app.name,
      icon: <Avatar name={app.name} src={app.iconUrl} size={16} square />,
      keywords: [app.slug, "open", "go to"],
      hint: app.slug === currentApp?.slug ? "Current" : undefined,
      onSelect: () => void navigate({
        to: "/$orgSlug/apps/$appSlug",
        params: {
          orgSlug,
          appSlug: app.slug
        }
      })
    })), {
      id: "apps:create",
      label: "Create application",
      icon: <PlusIcon size={15} />,
      keywords: ["new", "add"],
      onSelect: createApplication
    }, {
      id: "apps:all",
      label: "All applications",
      icon: <AppsIcon size={15} />,
      keywords: ["list"],
      onSelect: () => void navigate({
        to: "/$orgSlug/apps",
        params
      })
    }]
  });
  groups.push({
    label: "Go to",
    items: [{
      id: "nav:overview",
      label: "Overview",
      icon: <HomeIcon size={15} />,
      keywords: ["home", "dashboard", "stats"],
      onSelect: () => void navigate({
        to: "/$orgSlug",
        params
      })
    }, {
      id: "nav:api-keys",
      label: "API keys",
      icon: <KeyIcon size={15} />,
      keywords: ["token", "secret", "credentials", "developer"],
      onSelect: () => void navigate({
        to: "/$orgSlug/settings/api-keys",
        params
      })
    }, {
      id: "nav:developers",
      label: "Documentation",
      icon: <DocsIcon size={15} />,
      keywords: ["docs", "quickstart", "api", "developer", "snippets"],
      onSelect: () => void navigate({
        to: "/$orgSlug/developers",
        params
      })
    }, {
      id: "nav:members",
      label: "Members",
      icon: <UsersIcon size={15} />,
      keywords: ["team", "invite", "people", "roles"],
      onSelect: () => void navigate({
        to: "/$orgSlug/settings/members",
        params
      })
    }, {
      id: "nav:billing",
      label: "Billing",
      icon: <BillingIcon size={15} />,
      keywords: ["plan", "subscription", "invoice", "upgrade", "payment"],
      onSelect: () => void navigate({
        to: "/$orgSlug/settings/billing",
        params,
        search: {}
      })
    }, {
      id: "nav:settings",
      label: "Organization settings",
      icon: <SettingsIcon size={15} />,
      keywords: ["rename", "slug", "logo"],
      onSelect: () => void navigate({
        to: "/$orgSlug/settings",
        params
      })
    }, {
      id: "nav:account",
      label: "Account settings",
      icon: <PasskeyIcon size={15} />,
      keywords: ["passkey", "security", "profile", "sign in", "webauthn"],
      onSelect: () => void navigate({
        to: "/$orgSlug/settings/account",
        params
      })
    }]
  });
  groups.push({
    label: "Organizations",
    items: [...organizations.filter(org => org.slug !== orgSlug).map(org => ({
      id: `org:${org.slug}`,
      label: `Switch to ${org.name}`,
      icon: <Avatar name={org.name} src={org.logo} size={16} square />,
      keywords: [org.slug, "organization", "workspace", "team"],
      onSelect: () => void navigate({
        to: "/$orgSlug",
        params: {
          orgSlug: org.slug
        }
      })
    })), {
      id: "org:create",
      label: "Create organization",
      icon: <PlusIcon size={15} />,
      keywords: ["new", "workspace", "team"],
      onSelect: () => void navigate({
        to: "/onboarding"
      })
    }]
  });
  const themes: {
    value: ThemePreference;
    label: string;
    icon: ReactNode;
  }[] = [{
    value: "light",
    label: "Light theme",
    icon: <LightThemeIcon size={15} />
  }, {
    value: "dark",
    label: "Dark theme",
    icon: <DarkThemeIcon size={15} />
  }, {
    value: "system",
    label: "System theme",
    icon: <SystemThemeIcon size={15} />
  }];
  groups.push({
    label: "Preferences",
    items: [...themes.map(option => ({
      id: `theme:${option.value}`,
      label: option.label,
      icon: option.icon,
      keywords: ["appearance", "mode", "color scheme"],
      hint: option.value === theme ? <CheckIcon size={14} /> : undefined,
      onSelect: () => setThemePreference(option.value)
    })), {
      id: "account:sign-out",
      label: "Sign out",
      icon: <LogOutIcon size={15} />,
      keywords: ["log out", "logout", "account"],
      onSelect: () => void signOut()
    }]
  });
  return groups;
}
async function copySubscribeUrl(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Subscription link copied", url);
  } catch {
    toast.error("Couldn't copy the link", url);
  }
}
function readRecents(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    // Storage can be blocked (private mode, disabled site data).
    return [];
  }
}
function writeRecents(ids: string[]) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(ids));
  } catch {
    // Recents just won't persist past this tab.
  }
}